import { Stack, Typography } from '@mui/material';
import React from 'react';

import NoContracts from '../../assets/images/NoContracts';
import { colors } from '../../components';
import CommonIconAvatar from '../../components/ui-components/CommonIconAvatar';
import { useTranslation } from '../../utils/translations';

// Shared "open contract" arrow affordance rendered on both the active and closed contract cards.
export const ContractArrowIcon: React.FC = () => (
    <CommonIconAvatar
        icon="icon-arrow-up"
        iconPadding={1}
        iconBorderRadius={4}
        iconSize={16}
        iconWeight={600}
        iconRotate="rotate(45deg)"
        iconBgColor={colors.greyPreset[20]}
        iconColor={colors.fullBlack}
    />
);

interface ContractPaymentInfoRowProps {
    currentPaymentDueDate?: string;
    currentPaymentDue?: string;
    isCurrentDueActual?: boolean;
    isPastDueActual?: boolean;
    nextPayment?: string;
    amountLabel?: string;
    amount?: string;
    lastPayment?: string;
}

// Shared payment info footer rendered on both the active and closed contract cards: a top row
// of payment due date / loan-or-lease amount / amount due (falling back to the next invoice due
// date when no payment is currently due), plus a full-width bottom row for the last payment date.
export const ContractPaymentInfoRow: React.FC<ContractPaymentInfoRowProps> = ({
    currentPaymentDueDate,
    currentPaymentDue,
    isCurrentDueActual,
    isPastDueActual,
    nextPayment,
    amountLabel,
    amount,
    lastPayment,
}) => {
    const { t } = useTranslation();
    return (
        <>
            <Stack gap={0.5}>
                <Typography variant="body2" style={{ color: colors.greyPreset[100] }}>
                    {isCurrentDueActual ? t('contracts.list.paymentDueDate') : t('contracts.list.nextPaymentOn')}
                </Typography>
                <Typography variant="body1" color="textPrimary">
                    {isCurrentDueActual ? currentPaymentDueDate : nextPayment}
                </Typography>
            </Stack>
            <Stack gap={0.5} alignItems="flex-end" textAlign="right">
                <Typography variant="body2" style={{ color: colors.greyPreset[150] }}>
                    {isCurrentDueActual ? t('contracts.list.currentPaymentDue') : t('contracts.details.nextPaymentDue')}
                </Typography>
                <Typography variant="body1" fontWeight={700} color={isPastDueActual ? 'error' : 'textPrimary'}>
                    {currentPaymentDue}
                </Typography>
            </Stack>
            <Stack gap={0.5}>
                <Typography variant="body2" style={{ color: colors.greyPreset[100] }}>
                    {amountLabel}
                </Typography>
                <Typography variant="body1" color="textPrimary">
                    {amount}
                </Typography>
            </Stack>
            <Stack gap={0.5} alignItems="flex-end" textAlign="right">
                <Typography variant="body2" style={{ color: colors.greyPreset[100] }}>
                    {t('contracts.list.lastPaymentOn')}
                </Typography>
                <Typography variant="body1" color="textPrimary">
                    {lastPayment}
                </Typography>
            </Stack>
        </>
    );
};

interface NoContractsFoundProps {
    message: string;
    headingComponent?: React.ElementType;
}

// Shared "no contracts" empty state rendered on both the active and closed contract list tabs.
export const NoContractsFound: React.FC<NoContractsFoundProps> = ({ message, headingComponent }) => (
    <Stack height={'100%'}>
        <Stack
            justifyContent="center"
            alignItems="center"
            sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -10%)',
            }}
        >
            <NoContracts />
            <Typography
                variant="h6"
                mt={1.5}
                {...(headingComponent ? { component: headingComponent } : {})}
                color="textSecondary"
                textAlign={'center'}
            >
                {message}
            </Typography>
        </Stack>
    </Stack>
);
